import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createWidget } from 'actions/editor';
import { 
  fetchPaletteFolders, fetchComponentsForPalette, fetchComponentsForPaletteFolder 
} from 'actions/palette';

import { Tab } from 'react-bootstrap';
import _ from "lodash";
import cx from 'classnames';

import { PaletteContext } from '.';
import { getPositionForNewWidget } from 'components/Editor/helper/layoutManager';
import InfiniteScroll from "components/InfiniteScroll";
import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { ShortLoader } from 'components/ApplicationLoader';

import { TEXT } from 'constants/widgetTypes';

import styles from './palette.module.scss';
import { createMixpanelEvent } from 'actions/mixpanel';
import { MIXPANEL_BRANDKIT_CREATED, MIXPANEL_WIDGET_ADDED, MIXPANEL_WIDGET_USED } from 'constants/mixpanelEvents';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import StoryboardContext from 'components/Storyboard/context';

export const TextPalette = ({ editorSession, defaultParent }) => {
  const paletteContext = React.useContext(PaletteContext);

  const { palettes = [] } = paletteContext;

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });

  const paletteData = palettes.find(item => item.type === 'TEXT');
  const defaultId = _.get(paletteData, 'defaultId', null);
  const brandkitId = _.get(paletteData, 'brandkitId', null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const showDefault = _.get(paletteData, 'showDefault', false) || !brandkitEnabled;
  const loading = useSelector(state => { return _.get(state, `palette.brandkit[${brandkitId}].loading`) || false });
  const defaultLoading = useSelector(state => { return _.get(state, `palette.default[${defaultId}].loading`) || false });


  if(loading || defaultLoading) {
    return (
      <div className={styles.loaderContainer}>
        <ShortLoader />
      </div>
    );
  }


  return (
    <Tab.Pane eventKey="TEXT" className={cx(styles.tabPane, styles.textPalette)}>
      {brandkitEnabled &&
        <BrandkitTextPalette 
        parentWidget={parentWidget}
        editorSession={editorSession} 
        defaultParent={defaultParent} 
        paletteId={brandkitId} />
      }
      
      {showDefault &&
        <DefaultTextPalette
          parentWidget={parentWidget}
          editorSession={editorSession}
          defaultParent={defaultParent}
          paletteId={defaultId} />
      }
    </Tab.Pane>
  )
}

const BrandkitTextPalette = ({ editorSession, defaultParent, paletteId, parentWidget }) => {
  const dispatch = useDispatch();

  const paletteContext = React.useContext(PaletteContext);
  const storyboardContext = React.useContext(StoryboardContext);

  const components = useSelector(state => { return _.get(state, `palette.brandkit[${paletteId}].items`) || [] })
  const loading = useSelector(state => { return _.get(state, `palette.brandkit[${paletteId}].loading`) || false })
  const fetched = useSelector(state => { return _.get(state, `palette.brandkit[${paletteId}].fetched`) || false })
  const pagination = useSelector(state => { return _.get(state, `palette.brandkit[${paletteId}].pagination`) || {} })
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const fonts = components.map(component => {
    const widgets = _.get(component, 'content.widgets', []);
    if (!widgets) {
      return null;
    }
    const widget = widgets[0];
    return _.get(widget, 'fonts', null);
  }).filter(font => font).flat();
  Fonts.useFont(fonts);

  const desktopParentLayout = _.get(parentWidget, `properties.desktop.layout`);
  const mobileParentLayout = _.get(parentWidget, `properties.mobile.layout`);

  useEffect(() => {
    if (paletteId && !fetched) {
      handleComponentsForPalette(0);
    }
  }, [paletteId]);

  const handleComponentsForPalette = (page) => {
    var request = {
      sort: 'order',
      order: 'ASC'
    }
    dispatch(fetchComponentsForPalette(paletteId, 'brandkit', request, page, 50));
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleComponentsForPalette(nextPage);
  }

  const handleCreateText = (data) => {
    const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: 600, height: 50 });
    const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 335, height: 50 });

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: TEXT,
            name: data.name || "Text",
            properties: {
              desktop: {
                visible: true,
                ...data.properties.desktop,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                    rotate: "0deg",
                  },
                },
                alignment: 'left',
                lineSpacing: _.get(data, 'properties.desktop.font.lineSpacing', 1.2),
                letterSpacing: _.get(data, 'properties.desktop.font.letterSpacing', 0),
              },
              mobile: {
                visible: true,
                ...data.properties.mobile,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                    rotate: "0deg",
                  },
                },
                alignment: 'left',
                lineSpacing: _.get(data, 'properties.mobile.font.lineSpacing', 1.2),
                letterSpacing: _.get(data, 'properties.mobile.font.letterSpacing', 0),
              },
            },
            content: data.content,
          },
        ],
        defaultParent
      )
    );

    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
    mixpanelPayload = {
      ...mixpanelPayload,
      widget_type: 'Text',
      brandkit_widget: true
    }
    dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))

    paletteContext.close();
  }

  if (components.length === 0) {
    return null;
  }

  return (
    <div className={styles.defaultWidgets}>
      <div className={styles.paletteTitle}>
        <h3 className={styles.label}>BRAND TEXT</h3>
        <hr className={styles.labelHighliter} />
      </div>
      <InfiniteScroll
        className={cx(styles.scrollable)}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={loadMore}
      >
        {components.map(component => {
          const widgets = _.get(component, 'content.widgets', []);
          if (!widgets) {
            return null;
          }

          const widget = widgets[0];

          return (
            <div className={cx(styles.widget, styles.textWidget)} key={widget.id} onClick={() => {
              handleCreateText(widget);
            }}>
              <div dangerouslySetInnerHTML={{ __html: widget.content }} />
            </div>
          );
        })}
      </InfiniteScroll>
    </div>
  )
}

const DefaultTextPalette = ({ editorSession, defaultParent, paletteId, parentWidget }) => {
  const dispatch = useDispatch();

  const paletteContext = React.useContext(PaletteContext);
  const storyboardContext = React.useContext(StoryboardContext);

  const themes = useSelector(state => { return _.get(state, `${editorSession}.present.themes`) || [] })
  const components = useSelector(state => { return _.get(state, `palette.default[${paletteId}].items`) || [] })
  const fetched = useSelector(state => { return _.get(state, `palette.default[${paletteId}].fetched`) || false })

  const desktopParentLayout = _.get(parentWidget, `properties.desktop.layout`);
  const mobileParentLayout = _.get(parentWidget, `properties.mobile.layout`);

  useEffect(() => {
    if (paletteId && !fetched) {
      var request = {
        sort: 'order',
        order: 'ASC'
      }
      dispatch(fetchComponentsForPalette(paletteId, 'default', request, 0, 50));
    }
  }, [paletteId]);

  const handleCreateText = (data, themes) => {
    const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: 600, height: 50 });
    const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 335, height: 50 });

    var themeContent = createThemeContent(data, themes);

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: TEXT,
            name: "Text",
            properties: {
              desktop: {
                visible: true,
                ...data.properties.desktop,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                    rotate: "0deg",
                  },
                },
                alignment: 'left',
                lineSpacing: _.get(themes, 'font.lineSpacing', 1.2),
                letterSpacing: _.get(themes, 'font.letterSpacing', 0),
              },
              mobile: {
                visible: true,
                ...data.properties.mobile,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                    rotate: "0deg",
                  },
                },
                alignment: 'left',
                lineSpacing: _.get(themes, 'font.lineSpacing', 1.2),
                letterSpacing: _.get(themes, 'font.letterSpacing', 0),
              },
            },
            content: themeContent,
          },
        ],
        defaultParent
      )
    );
    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
    mixpanelPayload = {
      ...mixpanelPayload,
      widget_type: 'Text',
      brandkit_widget: false
    }
    dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))
    paletteContext.close();
  }

  const getTagNameFromContent = (content) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    return doc.body.firstChild.tagName.toLowerCase();
  }

  const createThemeContent = (data, theme) => {
    const fontStyles = _.get(theme, 'font');
    const textStyles = _.get(theme, 'font.style');

    if (!fontStyles) {
      return data.content;
    }

    let themeContent = "";

    switch (true) {
      case (textStyles.bold && textStyles.italic):
        themeContent = `
          <${theme.type}>
            <span style="font-weight: bold; font-style: italic;">${theme.label}</span>
          </${theme.type}>
        `
        break;

      case (textStyles.bold):
        themeContent = `
          <${theme.type}>
            <span style="font-weight: bold;">${theme.label}</span>
          </${theme.type}>
        `
        break;

      case (textStyles.italic):
        themeContent = `
          <${theme.type}>
            <span style="font-style: italic;">${theme.label}</span>
          </${theme.type}>
        `
        break;

      default:
        themeContent = `
          <${theme.type} style="font-size:${fontStyles.fontSize}px">${theme.label}</${theme.type}>
        `
        break;
    }

    return themeContent;
  }

  if(components.length === 0) {
    return null;
  }

  return (
    <div className={styles.defaultWidgets}>
      <div className={styles.paletteTitle}>
        <h3 className={styles.label}>CLEVERSTORY</h3>
        <hr className={styles.labelHighliter} />
      </div>
      {components.map(component => {
        const widgets = _.get(component, 'content.widgets', []);
        if (!widgets) {
          return null;
        }

        const widget = widgets[0];
        const tagName = getTagNameFromContent(widget.content);

        let textTheme = themes.find(data => data.type === tagName);

        var fontStyles = {
          fontFamily: _.get(textTheme, 'font.fontFamily', "Montserrat,sans-serif"),
          fontStyle: _.get(textTheme, 'font.style.italic', false) ? 'italic' : 'normal',
          fontWeight: _.get(textTheme, 'font.style.bold', false) ? 700 : 500
        }

        return (
          <div className={styles.widget} key={widget.id} onClick={() => {
            handleCreateText(widget, textTheme);
          }}>
            <div style={fontStyles} dangerouslySetInnerHTML={{ __html: widget.content }} />
          </div>
        );
      })}
    </div>
  )
}